import { useState, useEffect } from 'react';
import ct from 'countries-and-timezones';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const addPad = d => {
  return d.toString().padStart(2, '0');
};
const formatDateForTable = date => {
  const d = new Date(date);
  const dayOfMonth = addPad(d.getDate());
  const year = d.getFullYear();
  const hours = addPad(d.getHours());
  const minutes = addPad(d.getMinutes());
  const seconds = addPad(d.getSeconds());
  return `${dayOfMonth} ${months[d.getUTCMonth()]} ${year} - ${hours}:${minutes}:${seconds}`;
};
const formatUTCDateForTable = date => {
  const d = new Date(date);
  const dayOfMonth = addPad(d.getUTCDate());
  const year = d.getUTCFullYear();
  const hours = addPad(d.getUTCHours());
  const minutes = addPad(d.getUTCMinutes());
  const seconds = addPad(d.getUTCSeconds());
  return `${dayOfMonth} ${months[d.getUTCMonth()]} ${year} - ${hours}:${minutes}:${seconds}`;
};

const debounce = (func, delay) => {
  let inDebounce;
  return (...args) => {
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func(...args), delay);
  };
};

/* istanbul ignore file */

/**
 * Generate a random number (as string) to use for label/input connection
 *
 * @param {number} [size=1000000000]
 * @returns {string}
 */
const randomId = (size = 1000000000) => {
  return Math.floor(Math.random() * size).toString();
};

const throttle = (func, limit) => {
  let inThrottle = false;
  return (...args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

const wait = (delay, callback) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
      if (typeof callback === 'function') {
        callback();
      }
    }, delay);
  });
};

let TruncateType = /*#__PURE__*/function (TruncateType) {
  TruncateType["MIDDLE"] = "middle";
  TruncateType["END"] = "end";
  return TruncateType;
}({});
function splitFileName(filename) {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return {
      baseName: filename,
      extension: ''
    };
  }
  return {
    baseName: filename.slice(0, lastDotIndex),
    extension: filename.slice(lastDotIndex)
  };
}

/**
 * Truncate a string from the middle or end to a maximum length.
 * For files, the extension is preserved when truncated from the middle.
 * @param str - The string to truncate
 * @param maxCharactersToDisplay - The maximum length of the string (required)
 * @param type - The type of truncation to perform. Defaults to middle.
 */
function truncate(str, maxCharactersToDisplay, type = TruncateType.MIDDLE) {
  if (str.length <= maxCharactersToDisplay) return str;
  if (type === TruncateType.MIDDLE) {
    const {
      baseName,
      extension
    } = splitFileName(str);
    const extensionLength = extension.length;
    const availableLength = Math.max(maxCharactersToDisplay - extensionLength, 6);
    const ellipsisLength = 3;
    if (availableLength >= baseName.length) return str;
    const charsToKeepPerSide = Math.floor((availableLength - ellipsisLength) / 2);
    const truncatedBaseName = baseName.slice(0, charsToKeepPerSide) + '...' + baseName.slice(baseName.length - charsToKeepPerSide);
    return truncatedBaseName + extension;
  } else {
    return str.slice(0, maxCharactersToDisplay - 3) + '...';
  }
}

/* istanbul ignore file */

const getAbortController = () => {
  try {
    return new AbortController();
  } catch (e) {
    return {
      abort: () => {},
      signal: null
    };
  }
};
const DEFAULT_API_DELAY = 750;
let controller = getAbortController();
const cancelApiCalls = () => {
  // Set current controller to aborted then create a new controller
  // so we can send other calls after this. Timeout long enough for any grouped call
  // stays using the same controller but short enough that any user interaction gets
  // a new controller.
  controller.abort();
  setTimeout(() => {
    controller = getAbortController();
  }, 10);
};
const useApi = props => {
  const {
    initialUrl,
    initialOptions,
    initialData
  } = props || {};
  const [state, setState] = useState({
    isLoading: !!initialUrl,
    data: initialData,
    url: initialUrl
  });
  const straightApiCall = async (url, options) => {
    const {
      delay,
      ...callOptions
    } = options || {};
    setState(oldState => ({
      ...oldState,
      url,
      isLoading: true
    }));
    let response;
    let {
      data
    } = {
      ...state
    };
    let error;
    let newState = state;
    try {
      const requestPromise = fetch(url, {
        signal: controller.signal,
        ...callOptions
      });
      if (delay) {
        const combine = Promise.all([requestPromise, wait(DEFAULT_API_DELAY)]);
        [response] = await combine;
      } else {
        response = await requestPromise;
      }
      if (response.status < 400) {
        const contentType = response.headers.get('Content-Type');
        data = contentType?.includes('application/json') ? await response.json() : await response.text();
      }
    } catch (err) {
      error = err;
    } finally {
      setState(oldState => {
        newState = {
          ...oldState,
          isLoading: false,
          data,
          response,
          error
        };
        return newState;
      });
    }
    return newState;
  };
  const makeApiCall = async (url, options) => {
    const {
      debounce: debounceTime,
      throttle: throttleTime,
      ...callOptions
    } = options || {};
    if (debounceTime) {
      debounce(straightApiCall, debounceTime)(url, callOptions);
      return state;
    }
    if (throttleTime) {
      throttle(straightApiCall, throttleTime)(url, callOptions);
      return state;
    }
    return straightApiCall(url, options);
  };
  useEffect(() => {
    if (initialUrl) {
      makeApiCall(initialUrl, initialOptions);
    }
  }, []);
  return [state, makeApiCall];
};

// https://github.com/manuelmhtr/countries-and-timezones

const zoneInfo = ct.getAllTimezones();
const timeZones = Object.keys(zoneInfo).map(key => {
  const zone = zoneInfo[key];
  return {
    dstOffset: zone.dstOffset,
    dstOffsetString: zone.dstOffsetStr,
    label: key.split('/').join(' - ').split('_').join(' '),
    utcOffset: zone.utcOffset,
    utcOffsetString: zone.utcOffsetStr,
    value: key
  };
});
const useTimeZones = () => timeZones;

export { TruncateType, cancelApiCalls, debounce, formatDateForTable, formatUTCDateForTable, randomId, throttle, truncate, useApi, useTimeZones, wait };
